'use client';

import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@routly/ui';
import { Plus, Trash2 } from 'lucide-react';
import { rowKey } from '@/lib/rowKey';
import type { Counter } from '@/lib/types';

const TYPES: Counter['type'][] = ['duration', 'distance', 'route', 'parameter'];
const METHODS = ['first', 'last', 'min', 'max', 'avg', 'sum'];

/** A fresh row of each type: shared objects would alias between rows. */
const blank: Record<Counter['type'], () => Counter> = {
  duration: () => ({ type: 'duration', as: 'duration' }),
  distance: () => ({ type: 'distance', as: 'distance' }),
  route: () => ({ type: 'route', as: 'route' }),
  parameter: () => ({ type: 'parameter', sensor: 'speed', method: 'max', as: 'maxSpeed' }),
};

interface CounterFieldsProps {
  counters: Counter[];
  onChange: (counters: Counter[]) => void;
}

/**
 * What the interval remembers once it closes.
 *
 * These have to be chosen now, not queried later: the telemetry they are
 * computed from expires from ClickHouse after seven days, and an interval
 * without the counter is an interval whose figure is gone for good.
 */
export function CounterFields({ counters, onChange }: CounterFieldsProps) {
  const replace = (index: number, counter: Counter) =>
    onChange(counters.map((existing, i) => (i === index ? counter : existing)));

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Chosen now rather than queried later: the raw telemetry is kept seven days, so a figure
        nobody counted is a figure nobody can recover.
      </p>

      {counters.map((counter, index) => (
        <div key={rowKey(counter)} className="flex items-center gap-2">
          <Select
            value={counter.type}
            onValueChange={(value) => replace(index, blank[value as Counter['type']]())}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {counter.type === 'parameter' ? (
            <>
              <Input
                aria-label="Sensor"
                className="w-36 font-mono"
                placeholder="speed"
                value={counter.sensor}
                onChange={(event) => replace(index, { ...counter, sensor: event.target.value })}
              />
              <Select
                value={counter.method}
                onValueChange={(value) => replace(index, { ...counter, method: value })}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          ) : null}

          <Input
            aria-label="Stored as"
            className="w-40 font-mono"
            placeholder="maxSpeed"
            value={counter.as}
            onChange={(event) => replace(index, { ...counter, as: event.target.value })}
          />

          <Button
            variant="ghost"
            size="sm"
            aria-label={`Remove counter ${index + 1}`}
            onClick={() => onChange(counters.filter((_, i) => i !== index))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button variant="outline" size="sm" onClick={() => onChange([...counters, blank.duration()])}>
        <Plus className="mr-2 h-4 w-4" />
        Add counter
      </Button>
    </div>
  );
}
